import { createFileRoute, Link, notFound, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { formatBRL, formatRange } from "@/lib/format";
import { getBusinessJourneyPreview, startProject } from "@/lib/journey.functions";
import {
  Heart,
  Check,
  ArrowLeft,
  Calculator,
  ClipboardCheck,
  CalendarDays,
  UserSearch,
  Download,
  ExternalLink,
  AlertTriangle,
  TrendingUp,
  Wrench,
  Users,
  Sparkles,
  BookOpen,
  Rocket,
  ArrowRight,
} from "lucide-react";
import { resolveLegacySlug } from "@/lib/legacy-slugs";
import { getMethodologyByArchetype } from "@/lib/methodology/registry";
import { MethodologyOverview } from "@/components/members/MethodologyOverview";
import { track } from "@/hooks/useAnalytics";

export const Route = createFileRoute("/_authenticated/membros/negocios/$slug")({
  beforeLoad: ({ params }) => {
    const canonical = resolveLegacySlug(params.slug);
    if (canonical && canonical !== params.slug) {
      throw redirect({
        to: "/membros/negocios/$slug",
        params: { slug: canonical },
        replace: true,
      });
    }
  },
  head: ({ params }) => ({
    meta: [
      { title: `${params.slug} — Projeto 101` },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: BusinessDetail,
  errorComponent: ({ error, reset }) => (
    <div className="mx-auto max-w-2xl p-8">
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 text-red-600" size={20} />
          <div>
            <h1 className="font-display text-xl font-extrabold text-red-900">
              Não foi possível carregar este negócio
            </h1>
            <p className="mt-1 text-sm text-red-700">{error.message}</p>
            <div className="mt-4 flex gap-2">
              <button
                onClick={reset}
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white"
              >
                Tentar novamente
              </button>
              <Link
                to="/membros/negocios"
                className="rounded-md border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700"
              >
                Voltar à Biblioteca
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  ),
  notFoundComponent: () => <NotFoundView />,
});

function NotFoundView() {
  const { slug } = Route.useParams();
  return (
    <div className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center px-6 py-16 text-center">
      <div className="rounded-full bg-[var(--beige)] p-4">
        <BookOpen className="text-[var(--navy)]" size={28} />
      </div>
      <h1 className="mt-6 font-display text-3xl font-extrabold text-[var(--navy)]">
        Negócio não encontrado
      </h1>
      <p className="mt-2 max-w-md text-sm text-[var(--gray-ink)]">
        O negócio <span className="font-mono text-xs">{slug}</span> não existe ou foi
        renomeado. Volte à Biblioteca para explorar as 110 fichas disponíveis.
      </p>
      <Link
        to="/membros/negocios"
        className="mt-6 inline-flex items-center gap-2 rounded-md bg-[var(--navy)] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[var(--navy)]/90"
      >
        <ArrowLeft size={14} /> Voltar à Biblioteca
      </Link>
    </div>
  );
}

function LoadingView() {
  return (
    <div className="mx-auto max-w-5xl animate-pulse space-y-6 p-2">
      <div className="h-4 w-40 rounded bg-black/5" />
      <div className="h-40 rounded-2xl bg-black/5" />
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="h-32 rounded-2xl bg-black/5" />
        <div className="h-32 rounded-2xl bg-black/5" />
        <div className="h-32 rounded-2xl bg-black/5" />
      </div>
      <div className="h-64 rounded-2xl bg-black/5" />
    </div>
  );
}

function BusinessDetail() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();

  const { data: b, isLoading } = useQuery({
    queryKey: ["business", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("businesses")
        .select("*, category:categories(id,slug,name)")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw notFound();
      return data;
    },
  });

  const businessId = b?.id;

  const { data: badges } = useQuery({
    queryKey: ["biz-badges", businessId],
    enabled: !!businessId,
    queryFn: async () =>
      (await supabase
        .from("business_badges")
        .select("*")
        .eq("business_id", businessId!)
        .order("sort_order")).data ?? [],
  });

  const { data: insights } = useQuery({
    queryKey: ["biz-insights", businessId],
    enabled: !!businessId,
    queryFn: async () =>
      (await supabase
        .from("business_insights")
        .select("*")
        .eq("business_id", businessId!)
        .order("priority")).data ?? [],
  });

  const { data: checklist } = useQuery({
    queryKey: ["biz-checklist", businessId],
    enabled: !!businessId,
    queryFn: async () =>
      (await supabase
        .from("business_checklists")
        .select("*")
        .eq("business_id", businessId!)
        .order("order_index")).data ?? [],
  });

  const { data: references } = useQuery({
    queryKey: ["biz-refs", businessId],
    enabled: !!businessId,
    queryFn: async () =>
      (await supabase
        .from("business_references")
        .select("*")
        .eq("business_id", businessId!)).data ?? [],
  });

  const { data: downloads } = useQuery({
    queryKey: ["downloads-all"],
    queryFn: async () =>
      (await supabase
        .from("downloads")
        .select("*")
        .eq("active", true)
        .order("sort_order")).data ?? [],
  });

  const { data: fav } = useQuery({
    queryKey: ["fav-one", slug, businessId],
    enabled: !!businessId,
    queryFn: async () => {
      const { data } = await supabase
        .from("favorites")
        .select("business_id")
        .eq("business_id", businessId!)
        .maybeSingle();
      return !!data;
    },
  });

  const { data: prog } = useQuery({
    queryKey: ["prog-one", slug, businessId],
    enabled: !!businessId,
    queryFn: async () => {
      const { data } = await supabase
        .from("user_progress")
        .select("*")
        .eq("business_id", businessId!)
        .maybeSingle();
      return data;
    },
  });

  async function toggleFav() {
    if (!b) return;
    const { data: userRes } = await supabase.auth.getUser();
    const uid = userRes.user?.id;
    if (!uid) return;
    if (fav) {
      await supabase.from("favorites").delete().eq("user_id", uid).eq("business_id", b.id);
    } else {
      await supabase.from("favorites").insert({ user_id: uid, business_id: b.id });
    }
    qc.invalidateQueries({ queryKey: ["fav-one", slug] });
    qc.invalidateQueries({ queryKey: ["favorite-ids"] });
  }

  async function toggleAnalyzed() {
    if (!b) return;
    const { data: userRes } = await supabase.auth.getUser();
    const uid = userRes.user?.id;
    if (!uid) return;
    const nextAnalyzed = !prog?.analyzed;
    await supabase
      .from("user_progress")
      .upsert(
        { user_id: uid, business_id: b.id, analyzed: nextAnalyzed },
        { onConflict: "user_id,business_id" },
      );
    qc.invalidateQueries({ queryKey: ["prog-one", slug] });
    qc.invalidateQueries({ queryKey: ["progress-count"] });
  }

  const archetype = (b as any)?.archetype as string | null | undefined;
  const categoryKey = (b as any)?.category_key as string | null | undefined;
  const methodology = getMethodologyByArchetype(archetype ?? null);

  // Analytics — hooks devem vir antes de qualquer early return
  const viewedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!b || viewedRef.current === slug) return;
    viewedRef.current = slug;
    track("library_business_viewed", {
      business_slug: slug,
      category_slug: b.category?.slug ?? undefined,
      metadata: { archetype, category_key: categoryKey, has_methodology: !!methodology },
    });
    if (methodology) {
      track("methodology_previewed", {
        business_slug: slug,
        metadata: { methodology_id: methodology.id, archetype: methodology.archetype, category: methodology.category },
      });
    }
  }, [slug, b, methodology, archetype, categoryKey]);

  if (isLoading || !b) return <LoadingView />;

  const plan30 = normalizePlan(b.first_30_days_plan);
  const plan90 = normalizePlan(b.first_90_days_plan);
  const badgesList = badges ?? [];

  return (
    <article className="mx-auto max-w-5xl space-y-6 duration-300 animate-in fade-in">
      <Link
        to="/membros/negocios"
        className="inline-flex items-center gap-1 text-sm text-[var(--gray-ink)] transition hover:text-[var(--navy)]"
      >
        <ArrowLeft size={14} /> Voltar à Biblioteca
      </Link>

      {/* HERO */}
      <header className="overflow-hidden rounded-2xl border border-black/5 bg-gradient-to-br from-white to-[var(--beige)]/40 p-6 lg:p-8">
        <div className="flex flex-wrap items-center gap-2">
          {b.category?.name && (
            <span className="rounded-full bg-[var(--green)]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--green)]">
              {b.category.name}
            </span>
          )}
          {b.opportunity_score != null && (
            <span className="rounded-full bg-[var(--navy)]/10 px-3 py-1 text-xs font-semibold text-[var(--navy)]">
              Oportunidade {Number(b.opportunity_score).toFixed(0)}/100
            </span>
          )}
        </div>
        <h1 className="mt-3 font-display text-3xl font-extrabold text-[var(--navy)] lg:text-4xl">
          {b.title}
        </h1>
        {b.summary && (
          <p className="mt-3 max-w-3xl text-[var(--gray-ink)]">{b.summary}</p>
        )}

        {badgesList.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {badgesList.map((bg: any) => (
              <span
                key={bg.id}
                className="inline-flex items-center gap-1 rounded-full border border-black/5 bg-white px-2.5 py-1 text-xs text-[var(--navy)]"
              >
                <span>{bg.badge_icon}</span> {bg.badge_label}
              </span>
            ))}
          </div>
        )}

        <StartProjectCTA businessId={b.id} businessTitle={b.title} />

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            onClick={toggleFav}
            className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition ${
              fav
                ? "border-[var(--green)] bg-[var(--green)]/10 text-[var(--green)]"
                : "border-black/10 text-[var(--gray-ink)] hover:bg-black/5"
            }`}
          >
            <Heart size={14} fill={fav ? "currentColor" : "none"} />
            {fav ? "Favoritado" : "Favoritar"}
          </button>
          <button
            onClick={toggleAnalyzed}
            className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition ${
              prog?.analyzed
                ? "border-[var(--navy)] bg-[var(--navy)] text-white"
                : "border-black/10 text-[var(--gray-ink)] hover:bg-black/5"
            }`}
          >
            <Check size={14} />
            {prog?.analyzed ? "Analisado" : "Marcar como analisado"}
          </button>
        </div>
      </header>

      {methodology && (
        <MethodologyOverview
          methodology={methodology}
          onStart={() => {
            track("business_build_started", {
              business_slug: slug,
              metadata: { methodology_id: methodology.id, archetype: methodology.archetype },
            });
            const el = document.getElementById("start-project-cta");
            el?.scrollIntoView({ behavior: "smooth", block: "center" });
          }}
        />
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div className="space-y-6">
          {/* KPIs financeiros */}
          <section className="rounded-2xl border border-black/5 bg-white p-6">
            <h2 className="font-display text-lg font-extrabold text-[var(--navy)]">
              Números do negócio
            </h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <KPI label="Investimento inicial" value={formatRange(b.investment_min_cents, b.investment_max_cents)} />
              <KPI label="Capital de giro" value={formatRange(b.working_capital_min_cents, b.working_capital_max_cents)} />
              <KPI label="Payback" value={formatMonths(b.payback_months_min, b.payback_months_max)} />
              <KPI label="Ticket médio" value={formatBRL(b.ticket_average_cents ?? b.ticket_cents)} />
              <KPI label="Margem líquida" value={formatPctRange(b.net_margin_pct_min, b.net_margin_pct_max)} />
              <KPI label="Faturamento provável/mês" value={formatBRL(b.probable_revenue_monthly_cents)} />
              <KPI label="Custos fixos/mês" value={formatBRL(b.fixed_costs_monthly_cents)} />
              <KPI label="Ponto de equilíbrio" value={formatBRL(b.break_even_revenue_cents)} />
              <KPI label="1º cliente em" value={b.time_to_first_client_days ? `${b.time_to_first_client_days} dias` : "—"} />
            </div>
            {(b.working_capital_notes || b.payback_notes || b.break_even_notes) && (
              <div className="mt-4 space-y-1 text-xs text-[var(--gray-ink)]">
                {b.working_capital_notes && <p>· {b.working_capital_notes}</p>}
                {b.payback_notes && <p>· {b.payback_notes}</p>}
                {b.break_even_notes && <p>· {b.break_even_notes}</p>}
              </div>
            )}
          </section>

          <div className="grid gap-4 md:grid-cols-3">
            <Chip label="Dificuldade" value={b.difficulty} />
            <Chip label="Escalabilidade" value={b.scalability} />
            <Chip label="Modelo operacional" value={b.operation_type} />
            <Chip label="Público-alvo" value={b.target_market ?? b.public_target} />
            <Chip label="Recorrência" value={b.recurring_revenue_level} />
            <Chip label="Complexidade regulatória" value={b.regulatory_complexity} />
          </div>

          <Section title="O que este negócio resolve" icon={Sparkles}>
            <Text value={b.problem_solved ?? b.overview} />
          </Section>

          <Section title="Persona ideal" icon={Users}>
            <Text value={b.target_persona ?? b.public_target} />
            {b.not_recommended_for && (
              <div className="mt-3 rounded-md bg-red-50 p-3 text-xs text-red-800">
                <strong>Não recomendado para:</strong> {b.not_recommended_for}
              </div>
            )}
          </Section>

          <Section title="Posicionamento e diferenciais">
            <Text value={b.positioning} />
            {b.upsell_opportunities?.length ? (
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
                  Upsells possíveis
                </p>
                <List items={b.upsell_opportunities} />
              </div>
            ) : null}
          </Section>

          <Section title="Como começar (primeiro passo)">
            <p className="text-lg font-semibold text-[var(--green)]">{b.first_step ?? "—"}</p>
            {b.validation_7days && (
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
                  Validação em 7 dias
                </p>
                <Text value={b.validation_7days} />
              </div>
            )}
          </Section>

          {plan30.length > 0 && (
            <Section title="Plano dos primeiros 30 dias" icon={CalendarDays}>
              <PlanList items={plan30} />
            </Section>
          )}

          {plan90.length > 0 && (
            <Section title="Plano dos primeiros 90 dias" icon={CalendarDays}>
              <PlanList items={plan90} />
            </Section>
          )}

          <Section title="Primeiros clientes" icon={UserSearch}>
            {b.potential_clients?.length ? (
              <>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
                  Clientes potenciais
                </p>
                <List items={b.potential_clients} />
              </>
            ) : null}
            {b.acquisition_channels?.length ? (
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
                  Como conseguir clientes
                </p>
                <List items={b.acquisition_channels} />
              </div>
            ) : null}
            {b.sales_script && (
              <div className="mt-4 rounded-md bg-[var(--beige)]/50 p-3 text-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
                  Script de venda
                </p>
                <p className="mt-1 whitespace-pre-line">{b.sales_script}</p>
              </div>
            )}
            {b.common_objections?.length ? (
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
                  Objeções comuns
                </p>
                <List items={b.common_objections} />
              </div>
            ) : null}
          </Section>

          {b.recommended_tools?.length ? (
            <Section title="Ferramentas recomendadas" icon={Wrench}>
              <List items={b.recommended_tools} />
            </Section>
          ) : null}

          {b.key_metrics?.length ? (
            <Section title="Métricas-chave" icon={TrendingUp}>
              <List items={b.key_metrics} />
            </Section>
          ) : null}

          {checklist && checklist.length > 0 && (
            <Section title="Checklist inicial" icon={ClipboardCheck}>
              <ChecklistView items={checklist} />
            </Section>
          )}

          {insights && insights.length > 0 && (
            <Section title="Insights e erros comuns">
              <div className="space-y-3">
                {insights.map((ins: any) => (
                  <div
                    key={ins.id}
                    className={`rounded-lg border p-3 text-sm ${insightStyle(ins.insight_type)}`}
                  >
                    <p className="text-xs font-semibold uppercase tracking-wide opacity-80">
                      {ins.insight_title}
                    </p>
                    <p className="mt-1">{ins.insight_text}</p>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <Section title="Rotina, materiais e estrutura">
            {b.routine && (
              <>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Rotina</p>
                <Text value={b.routine} />
              </>
            )}
            {b.materials && (
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Materiais</p>
                <Text value={b.materials} />
              </div>
            )}
            {b.process && (
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Processo</p>
                <Text value={b.process} />
              </div>
            )}
          </Section>

          <Section title="Riscos e regulação" icon={AlertTriangle}>
            {b.risks && (
              <>
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Riscos</p>
                <Text value={b.risks} />
              </>
            )}
            {b.licenses && (
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Licenças</p>
                <Text value={b.licenses} />
              </div>
            )}
            {b.regulatory_notes && (
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">Notas regulatórias</p>
                <Text value={b.regulatory_notes} />
              </div>
            )}
          </Section>

          {references && references.length > 0 && (
            <Section title="Referências">
              <ul className="space-y-2 text-sm">
                {references.map((r: any) => (
                  <li key={r.id}>
                    {r.url ? (
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[var(--green)] hover:underline"
                      >
                        {r.title} <ExternalLink size={12} />
                      </a>
                    ) : (
                      <span className="font-medium">{r.title}</span>
                    )}
                    {r.organization && (
                      <span className="text-[var(--gray-ink)]"> — {r.organization}</span>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {b.disclaimer && (
            <p className="rounded-lg bg-[var(--beige)] p-4 text-xs text-[var(--gray-ink)]">
              <strong>Aviso:</strong> {b.disclaimer}
            </p>
          )}
        </div>

        {/* SIDEBAR */}
        <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
          <div className="rounded-2xl border border-black/5 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
              Aplicar a este negócio
            </p>
            <div className="mt-3 space-y-2">
              <SideLink
                to="/membros/calculadora-10k"
                icon={Calculator}
                label="Calculadora 10K"
                sub="Simule R$ 10 mil de faturamento"
              />
              <SideLink
                to="/membros/validacao"
                icon={ClipboardCheck}
                label="Validação 7 dias"
                sub="Roteiro para testar demanda"
              />
              <SideLink
                to="/membros/plano-30-dias"
                icon={CalendarDays}
                label="Plano 30 dias"
              />
              <SideLink
                to="/membros/primeiros-clientes"
                icon={UserSearch}
                label="Primeiros clientes"
              />
              <SideLink to="/membros/downloads" icon={Download} label="Downloads" />
              <SideLink to="/membros/favoritos" icon={Heart} label="Favoritos" />
            </div>
          </div>

          {downloads && downloads.length > 0 && (
            <div className="rounded-2xl border border-black/5 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
                Materiais recomendados
              </p>
              <ul className="mt-3 space-y-2">
                {downloads.slice(0, 4).map((d: any) => (
                  <li key={d.id} className="text-sm">
                    <Link
                      to="/membros/downloads"
                      className="flex items-start gap-2 rounded-md p-2 hover:bg-black/5"
                    >
                      <Download size={14} className="mt-0.5 text-[var(--navy)]" />
                      <span>
                        <span className="font-semibold text-[var(--navy)]">{d.title}</span>
                        {d.description && (
                          <span className="mt-0.5 block text-xs text-[var(--gray-ink)] line-clamp-2">
                            {d.description}
                          </span>
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </article>
  );
}

/* ---------------- Helpers & subcomponents ---------------- */

function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-black/5 bg-white p-6">
      <h2 className="flex items-center gap-2 font-display text-lg font-extrabold text-[var(--navy)]">
        {Icon && <Icon size={16} className="text-[var(--green)]" />}
        {title}
      </h2>
      <div className="mt-3 text-sm text-[var(--gray-ink)]">{children}</div>
    </section>
  );
}

function KPI({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-black/5 bg-[var(--gray-soft)]/40 p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
        {label}
      </p>
      <p className="mt-1 font-display text-base font-extrabold text-[var(--navy)]">
        {value ?? "—"}
      </p>
    </div>
  );
}

function Chip({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
        {label}
      </p>
      <p className="mt-1 font-display text-base font-extrabold capitalize text-[var(--navy)]">
        {value ?? "—"}
      </p>
    </div>
  );
}

function Text({ value }: { value: string | null | undefined }) {
  if (!value) return <span className="text-[var(--gray-ink)]/70">—</span>;
  return <p className="whitespace-pre-line">{value}</p>;
}

function List({ items }: { items: string[] | null | undefined }) {
  if (!items || items.length === 0) return <span>—</span>;
  return (
    <ul className="mt-1 list-disc space-y-1 pl-5">
      {items.map((i, idx) => (
        <li key={idx}>{i}</li>
      ))}
    </ul>
  );
}

function SideLink({
  to,
  icon: Icon,
  label,
  sub,
}: {
  to: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  sub?: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-start gap-3 rounded-md p-2 transition hover:bg-black/5"
    >
      <Icon size={16} className="mt-0.5 text-[var(--green)]" />
      <span>
        <span className="block text-sm font-semibold text-[var(--navy)]">{label}</span>
        {sub && <span className="block text-xs text-[var(--gray-ink)]">{sub}</span>}
      </span>
    </Link>
  );
}

function ChecklistView({ items }: { items: any[] }) {
  const groups = items.reduce<Record<string, any[]>>((acc, it) => {
    (acc[it.category] ??= []).push(it);
    return acc;
  }, {});
  const labels: Record<string, string> = {
    documentacao: "Documentação",
    financeiro: "Financeiro",
    operacao: "Operação",
    vendas: "Vendas",
    marketing: "Marketing",
    compliance: "Compliance",
    estrutura: "Estrutura",
  };
  return (
    <div className="space-y-4">
      {Object.entries(groups).map(([cat, list]) => (
        <div key={cat}>
          <p className="text-xs font-semibold uppercase tracking-wide text-[var(--gray-ink)]">
            {labels[cat] ?? cat}
          </p>
          <ul className="mt-1 space-y-1">
            {list.map((c) => (
              <li key={c.id} className="flex items-start gap-2 text-sm">
                <Check size={14} className="mt-0.5 shrink-0 text-[var(--green)]" />
                <span>{c.item}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function PlanList({ items }: { items: Array<{ title?: string; text: string }> }) {
  return (
    <ol className="space-y-2">
      {items.map((it, idx) => (
        <li key={idx} className="flex gap-3 text-sm">
          <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--navy)] text-xs font-bold text-white">
            {idx + 1}
          </span>
          <span>
            {it.title && <span className="font-semibold text-[var(--navy)]">{it.title}: </span>}
            {it.text}
          </span>
        </li>
      ))}
    </ol>
  );
}

function normalizePlan(raw: any): Array<{ title?: string; text: string }> {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === "string") return { text: item };
        if (item && typeof item === "object") {
          const text = item.text ?? item.description ?? item.item ?? item.action ?? "";
          const title = item.title ?? item.week ?? item.day ?? item.phase;
          return text ? { title, text: String(text) } : null;
        }
        return null;
      })
      .filter(Boolean) as Array<{ title?: string; text: string }>;
  }
  if (typeof raw === "object") {
    return Object.entries(raw).map(([k, v]) => ({
      title: k,
      text: typeof v === "string" ? v : JSON.stringify(v),
    }));
  }
  return [];
}

function formatMonths(min?: number | null, max?: number | null): string {
  if (min == null && max == null) return "—";
  if (min != null && max != null) return `${min}–${max} meses`;
  return `${min ?? max} meses`;
}
function formatPctRange(min?: number | null, max?: number | null): string {
  if (min == null && max == null) return "—";
  if (min != null && max != null) return `${Number(min).toFixed(0)}–${Number(max).toFixed(0)}%`;
  return `${Number(min ?? max).toFixed(0)}%`;
}
function insightStyle(type: string): string {
  switch (type) {
    case "risk":
    case "warning":
      return "border-red-200 bg-red-50 text-red-900";
    case "advantage":
      return "border-emerald-200 bg-emerald-50 text-emerald-900";
    case "mistake":
      return "border-amber-200 bg-amber-50 text-amber-900";
    case "playbook":
    case "strategy":
      return "border-[var(--navy)]/20 bg-[var(--navy)]/5 text-[var(--navy)]";
    default:
      return "border-black/5 bg-[var(--gray-soft)]/40 text-[var(--navy)]";
  }
}

function StartProjectCTA({ businessId, businessTitle }: { businessId: string; businessTitle: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fetchPreview = useServerFn(getBusinessJourneyPreview);
  const runStart = useServerFn(startProject);

  const { data: preview } = useQuery({
    queryKey: ["biz-journey-preview", businessId],
    queryFn: () => fetchPreview({ data: { businessId } }),
    enabled: !!businessId,
  });

  const start = useMutation({
    mutationFn: async (force: boolean) => runStart({ data: { businessId, force } }),
    onSuccess: (res: any) => {
      if (res?.conflict) {
        setConfirmOpen(true);
        return;
      }
      qc.invalidateQueries({ queryKey: ["active-project"] });
      qc.invalidateQueries({ queryKey: ["biz-journey-preview", businessId] });
      toast.success("Projeto criado! Vamos começar.");
      navigate({ to: "/membros/meu-projeto" });
    },
    onError: (e: any) => toast.error(e?.message ?? "Não foi possível criar o projeto."),
  });

  const isCurrent = preview?.isCurrentProject;
  const hasOther = preview?.hasActiveProject && !isCurrent;
  const stageCount = preview?.stages?.length ?? 0;
  const missionCount = preview?.missions?.length ?? 0;

  return (
    <div className="mt-6 rounded-2xl border border-[var(--green)]/20 bg-gradient-to-br from-[var(--green)]/[0.06] to-transparent p-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Rocket size={16} className="text-[var(--green)]" />
            <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--green)]">
              {isCurrent ? "Seu projeto atual" : "Sistema de Implantação"}
            </p>
          </div>
          <p className="mt-1 font-display text-lg font-extrabold text-[var(--navy)]">
            {isCurrent
              ? "Continue construindo este negócio"
              : "Quero começar este negócio"}
          </p>
          <p className="mt-1 text-xs text-[var(--gray-ink)]">
            {stageCount} etapas · {missionCount} missões · cronograma passo a passo até sua primeira venda.
          </p>
        </div>
        {isCurrent ? (
          <Link
            to="/membros/meu-projeto"
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--green)] px-5 py-3 text-sm font-semibold text-white hover:bg-[var(--green)]/90"
          >
            Continuar projeto <ArrowRight size={14} />
          </Link>
        ) : (
          <button
            onClick={() => {
              if (hasOther) {
                setConfirmOpen(true);
              } else {
                start.mutate(false);
              }
            }}
            disabled={start.isPending}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--navy)] px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-[var(--navy)]/20 transition hover:-translate-y-0.5 disabled:opacity-50"
          >
            {start.isPending ? "Criando projeto..." : "Quero começar este negócio"}
            <ArrowRight size={14} />
          </button>
        )}
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setConfirmOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
          >
            <h3 className="font-display text-lg font-extrabold text-[var(--navy)]">
              Vamos transformar esta ideia no seu projeto?
            </h3>
            <p className="mt-2 text-sm text-[var(--gray-ink)]">
              Você está prestes a escolher <strong>{businessTitle}</strong> como seu
              projeto principal. A plataforma organizará um cronograma personalizado com{" "}
              {stageCount} etapas e {missionCount} missões.
            </p>
            {hasOther && (
              <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
                Você já possui um projeto ativo. Ao continuar, ele será arquivado no seu
                histórico (o progresso atual será preservado).
              </div>
            )}
            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                onClick={() => setConfirmOpen(false)}
                className="rounded-lg border border-black/10 px-4 py-2 text-sm font-semibold text-[var(--gray-ink)] hover:bg-[var(--gray-soft)]"
              >
                Continuar explorando
              </button>
              <button
                onClick={() => {
                  setConfirmOpen(false);
                  start.mutate(true);
                }}
                disabled={start.isPending}
                className="rounded-lg bg-[var(--navy)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--navy)]/90 disabled:opacity-50"
              >
                Criar meu projeto
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
